import React, { useState, useRef, useEffect, useMemo, KeyboardEvent, ChangeEvent } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../types/admin';
import { useAdminCompany } from '../contexts/AdminCompanyContext';

interface CompanySelectorProps {
  brand: BrandConfig;
  Icons: IconsType;
}

interface CompanyOption {
  id: string | null;
  displayName: string;
  name?: string;
  isAllOption?: boolean;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ brand, Icons }) => {
  const { companies, selectedCompanyId, selectedCompany, selectCompany, clearCompany, isLoading } = useAdminCompany();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter companies based on search query
  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return companies;
    const query = searchQuery.toLowerCase();
    return companies.filter((company: CompanyOption) =>
      company.displayName.toLowerCase().includes(query) ||
      (company.name && company.name.toLowerCase().includes(query))
    );
  }, [companies, searchQuery]);

  // Build options list with "All Companies" first
  const allOptions = useMemo((): CompanyOption[] => {
    const showAllCompanies = !searchQuery.trim() || 'all companies'.includes(searchQuery.toLowerCase());
    const baseOptions: CompanyOption[] = showAllCompanies ? [{ id: null, displayName: 'All Companies', isAllOption: true }] : [];
    return [...baseOptions, ...filteredCompanies];
  }, [filteredCompanies, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset highlighted index when filtered results change
  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- reset navigation index on search change */
  useEffect(() => { setHighlightedIndex(-1); }, [searchQuery]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      setHighlightedIndex(-1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev =>
        prev < allOptions.length - 1 ? prev + 1 : prev
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault();
      const option = allOptions[highlightedIndex];
      handleSelect(option.id);
    }
  };

  const handleSelect = (companyId: string | null) => {
    selectCompany(companyId);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    clearCompany();
    setSearchQuery('');
  };

  const handleTriggerClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  };

  if (isLoading) {
    return (
      <div
        className="px-3 py-2 text-sm rounded-lg border animate-pulse"
        style={{
          borderColor: brand.colors.border,
          backgroundColor: brand.colors.bgLight,
          minWidth: '220px',
          height: '38px'
        }}
      />
    );
  }

  return (
    <div className="relative" ref={dropdownRef} data-testid="company-selector">
      {/* Trigger Button */}
      <button
        onClick={handleTriggerClick}
        className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors hover:bg-gray-50"
        style={{
          borderColor: isOpen ? brand.colors.primary : brand.colors.border,
          backgroundColor: 'white',
          minWidth: '220px'
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Icons.Building className="w-4 h-4 shrink-0" style={{ color: brand.colors.textLight }} />
        <span
          className="flex-1 text-left truncate"
          style={{ color: selectedCompany ? brand.colors.text : brand.colors.textLight }}
        >
          {selectedCompany ? selectedCompany.displayName : 'All Companies'}
        </span>
        {selectedCompany ? (
          <button
            onClick={handleClear}
            className="p-0.5 rounded-sm hover:bg-gray-200 transition-colors"
            aria-label="Clear company selection"
            data-testid="company-selector-clear"
          >
            <Icons.X className="w-4 h-4" style={{ color: brand.colors.textLight }} />
          </button>
        ) : (
          <Icons.ChevronDown
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            style={{ color: brand.colors.textLight }}
          />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border z-50"
          style={{ borderColor: brand.colors.border, minWidth: '220px' }}
          role="listbox"
          onKeyDown={handleKeyDown}
        >
          {/* Search Input */}
          <div className="p-2 border-b" style={{ borderColor: brand.colors.border }}>
            <div className="relative">
              <Icons.Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: brand.colors.textLight }}
              />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                aria-label="Search companies"
                placeholder="Search companies..."
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-sm border focus:outline-hidden focus:ring-2 focus:ring-offset-0"
                style={{
                  borderColor: brand.colors.border,
                  color: brand.colors.text,
                }}
                data-testid="company-selector-search"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto py-1">
            {allOptions.length === 0 ? (
              <div
                className="px-3 py-2 text-sm text-center"
                style={{ color: brand.colors.textLight }}
              >
                No companies found
              </div>
            ) : (
              allOptions.map((option, index) => {
                const isSelected = option.isAllOption
                  ? !selectedCompanyId
                  : selectedCompanyId === option.id;
                const isHighlighted = index === highlightedIndex;

                return (
                  <button
                    key={option.id || 'all'}
                    onClick={() => handleSelect(option.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                      isHighlighted ? 'bg-blue-100' : isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {isSelected && (
                      <Icons.Check className="w-4 h-4 shrink-0" style={{ color: brand.colors.link }} />
                    )}
                    <span
                      className={isSelected ? 'ml-0' : 'ml-6'}
                      style={{ color: brand.colors.text }}
                    >
                      {option.displayName}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
